/**
 * @file Input.jsx
 * @description Core input component for Inspection Butler.
 *
 * @param {'text'|'email'|'password'|'number'|'tel'|'url'|'search'} [type='text']
 * @param {'sm'|'md'|'lg'} [size='md']
 * @param {string} [label] - Label text displayed above the input
 * @param {string} [helperText] - Helper text displayed below the input
 * @param {string} [error] - Error message (overrides helperText, turns border red)
 * @param {string} [successMessage] - Success text, turns border green
 * @param {boolean} [disabled=false]
 * @param {boolean} [readOnly=false]
 * @param {React.ReactNode} [leftIcon] - Icon element inside left of input
 * @param {React.ReactNode} [rightIcon] - Icon element inside right of input
 * @param {React.ReactNode} [leftAddon] - Addon element (like "$") at left edge
 * @param {React.ReactNode} [rightAddon] - Addon element (like ".com") at right edge
 * @param {number} [maxLength] - If set, shows character counter
 * @param {boolean} [clearable=false] - Show X button (auto-enabled for search)
 * @param {Function} [onClear] - Callback when clear button is clicked
 * @param {Function} [mask] - Input mask function(value) => maskedValue
 * @param {string} [className]
 */
import React, { useState, useRef, useId, useCallback } from 'react';

/** Eye icon for password visibility toggle */
function EyeIcon({ open }) {
  if (open) {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  }
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

/** X icon for clear button */
function ClearIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

const sizeStyles = {
  sm: {
    input: {
      minHeight: 'var(--input-height-sm)',
      padding: '0 var(--space-3)',
      fontSize: 'var(--text-body-sm)',
    },
    label: { fontSize: 'var(--text-caption)' },
  },
  md: {
    input: {
      minHeight: 'var(--input-height-md)',
      padding: '0 var(--space-3)',
      fontSize: 'var(--text-body-md)',
    },
    label: { fontSize: 'var(--text-body-sm)' },
  },
  lg: {
    input: {
      minHeight: 'var(--input-height-lg)',
      padding: '0 var(--space-4)',
      fontSize: 'var(--text-body-md)',
    },
    label: { fontSize: 'var(--text-body-sm)' },
  },
};

const Input = React.forwardRef(function Input(props, ref) {
  const {
    type = 'text',
    size = 'md',
    label,
    helperText,
    error,
    successMessage,
    disabled = false,
    readOnly = false,
    leftIcon,
    rightIcon,
    leftAddon,
    rightAddon,
    maxLength,
    clearable: clearableProp,
    onClear,
    mask,
    className = '',
    value,
    defaultValue,
    onChange,
    id: idProp,
    style: userStyle,
    ...rest
  } = props;

  const generatedId = useId();
  const inputId = idProp || generatedId;
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-error`;

  const innerRef = useRef(null);
  const inputRef = ref || innerRef;

  const [focused, setFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [internalValue, setInternalValue] = useState(defaultValue || '');

  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internalValue;
  const isSearch = type === 'search';
  const clearable = clearableProp ?? isSearch;
  const isPassword = type === 'password';
  const hasError = Boolean(error);
  const hasSuccess = Boolean(successMessage) && !hasError;

  const handleChange = useCallback((e) => {
    let val = e.target.value;
    if (mask) {
      val = mask(val);
      e.target.value = val;
    }
    if (!isControlled) {
      setInternalValue(val);
    }
    if (onChange) onChange(e);
  }, [mask, isControlled, onChange]);

  const handleClear = useCallback(() => {
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype, 'value'
    ).set;
    if (inputRef.current && nativeInputValueSetter) {
      nativeInputValueSetter.call(inputRef.current, '');
      const event = new Event('input', { bubbles: true });
      inputRef.current.dispatchEvent(event);
    }
    if (!isControlled) {
      setInternalValue('');
    }
    if (onClear) onClear();
    if (inputRef.current) inputRef.current.focus();
  }, [inputRef, isControlled, onClear]);

  // Border color based on state
  let borderColor = 'var(--border-default)';
  if (hasError) borderColor = 'var(--border-error)';
  else if (hasSuccess) borderColor = 'var(--border-success)';
  else if (focused) borderColor = 'var(--border-focus)';

  let ringStyle = {};
  if (focused && !hasError) ringStyle = { boxShadow: 'var(--focus-ring)' };
  if (focused && hasError) ringStyle = { boxShadow: 'var(--focus-ring-error)' };

  const wrapperStyle = {
    display: 'flex',
    alignItems: 'stretch',
    border: `2px solid ${borderColor}`,
    borderRadius: 'var(--radius-lg)',
    backgroundColor: disabled ? 'var(--surface-disabled)' : 'var(--surface-raised)',
    transition: `border-color var(--transition-fast) var(--ease-default), box-shadow var(--transition-fast) var(--ease-default)`,
    ...ringStyle,
  };

  const inputStyle = {
    flex: 1,
    minWidth: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily: 'var(--font-family)',
    color: disabled ? 'var(--text-disabled)' : 'var(--text-primary)',
    ...(sizeStyles[size]?.input || sizeStyles.md.input),
    ...(leftIcon ? { paddingLeft: 'var(--space-10)' } : {}),
    ...(rightIcon || isPassword || (clearable && currentValue) ? { paddingRight: 'var(--space-10)' } : {}),
  };

  const labelStyle = {
    display: 'block',
    marginBottom: 'var(--space-1_5)',
    fontWeight: 'var(--weight-medium)',
    color: hasError ? 'var(--text-error)' : 'var(--text-primary)',
    ...(sizeStyles[size]?.label || sizeStyles.md.label),
  };

  const helperStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 'var(--space-1_5)',
    fontSize: 'var(--text-caption)',
    color: hasError ? 'var(--text-error)' : hasSuccess ? 'var(--text-success)' : 'var(--text-secondary)',
  };

  const iconBaseStyle = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--text-tertiary)',
    pointerEvents: 'none',
  };

  const actionBtnStyle = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    right: 'var(--space-3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 'var(--space-1)',
    borderRadius: 'var(--radius-sm)',
    color: 'var(--text-tertiary)',
    minWidth: '28px',
    minHeight: '28px',
  };

  const addonStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '0 var(--space-3)',
    backgroundColor: 'var(--surface-sunken)',
    color: 'var(--text-secondary)',
    fontSize: 'var(--text-body-sm)',
    fontWeight: 'var(--weight-medium)',
    whiteSpace: 'nowrap',
    userSelect: 'none',
  };

  const describedBy = [
    hasError ? errorId : null,
    helperText && !hasError ? helperId : null,
  ].filter(Boolean).join(' ') || undefined;

  const showClear = clearable && currentValue && currentValue.length > 0 && !disabled && !readOnly;
  const showPasswordToggle = isPassword && !disabled;

  return (
    <div className={`ib-input-group ${className}`} style={userStyle}>
      {label && (
        <label htmlFor={inputId} style={labelStyle}>
          {label}
        </label>
      )}

      <div style={wrapperStyle}>
        {leftAddon && (
          <div style={{ ...addonStyle, borderRight: '2px solid var(--border-default)', borderRadius: 'var(--radius-lg) 0 0 var(--radius-lg)' }}>
            {leftAddon}
          </div>
        )}

        <div style={{ position: 'relative', flex: 1, display: 'flex' }}>
          {leftIcon && (
            <span style={{ ...iconBaseStyle, left: 'var(--space-3)' }}>
              {leftIcon}
            </span>
          )}

          <input
            ref={inputRef}
            id={inputId}
            type={isPassword && showPassword ? 'text' : type}
            value={isControlled ? value : undefined}
            defaultValue={isControlled ? undefined : defaultValue}
            onChange={handleChange}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            disabled={disabled}
            readOnly={readOnly}
            maxLength={maxLength}
            aria-invalid={hasError || undefined}
            aria-describedby={describedBy}
            style={inputStyle}
            {...rest}
          />

          {rightIcon && !showClear && !showPasswordToggle && (
            <span style={{ ...iconBaseStyle, right: 'var(--space-3)' }}>
              {rightIcon}
            </span>
          )}

          {showPasswordToggle && (
            <button
              type="button"
              onClick={() => setShowPassword(prev => !prev)}
              style={actionBtnStyle}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              <EyeIcon open={showPassword} />
            </button>
          )}

          {showClear && !showPasswordToggle && (
            <button
              type="button"
              onClick={handleClear}
              style={actionBtnStyle}
              aria-label="Clear input"
              tabIndex={-1}
            >
              <ClearIcon />
            </button>
          )}
        </div>

        {rightAddon && (
          <div style={{ ...addonStyle, borderLeft: '2px solid var(--border-default)', borderRadius: '0 var(--radius-lg) var(--radius-lg) 0' }}>
            {rightAddon}
          </div>
        )}
      </div>

      {(helperText || error || successMessage || maxLength) && (
        <div style={helperStyle}>
          <span id={hasError ? errorId : helperId} role={hasError ? 'alert' : undefined}>
            {hasError ? error : hasSuccess ? successMessage : helperText}
          </span>
          {maxLength && (
            <span style={{ color: 'var(--text-tertiary)', marginLeft: 'var(--space-2)' }}>
              {(currentValue || '').length}/{maxLength}
            </span>
          )}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
